'use client';

import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { toast } from '@/app/components/ui/use-toast';
import { useClaw } from '@/app/hooks';

const CLAW_INACTIVE_REASON = 'claw_is_inactive';
// 短窗口去重：同一次用户操作可能并发触发多个请求（如多文件上传），
// 避免同一时间叠加多个 toast 与重复刷新 claws。
const HANDLE_DEDUPE_WINDOW_MS = 3000;
let lastHandledAt = 0;

/**
 * 判断接口返回的 error 是否为 "claw 已失效"。
 * 后端约定：code=400 + reason='claw_is_inactive'，单看 reason 已足够区分。
 */
const isClawInactiveError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  return (error as { reason?: string }).reason === CLAW_INACTIVE_REASON;
};

/**
 * Claw 失效统一处理：
 * - 命中 claw_is_inactive 时 toast 提示用户
 * - 触发 getMyClaws 刷新当前 claw 状态，让后续 UI 进入到失效态
 *
 * 返回的 handleClawInactiveError 会在命中时返回 true，调用方可据此决定是否抑制各自的兜底报错。
 */
export const useClawInactiveHandler = () => {
  const t = useTranslations();
  const { getMyClaws } = useClaw();

  const handleClawInactiveError = useMemoizedFn((error: unknown): boolean => {
    if (!isClawInactiveError(error)) return false;

    const now = Date.now();
    if (now - lastHandledAt < HANDLE_DEDUPE_WINDOW_MS) {
      return true;
    }
    lastHandledAt = now;

    toast({
      type: 'error',
      message: t('claw.clawInactive'),
    });
    getMyClaws();
    return true;
  });

  return { handleClawInactiveError };
};
